/**
 * MESSAGING CONTEXT - Fan Side Real-Time Messaging
 *
 * Architecture: WebSocket-first with REST fallback, optimistic updates
 * State: Single conversation per fan with messages[]
 *
 * CRITICAL:
 * - Conversation must exist before sending (auto-created on first message)
 * - ALWAYS use SERVER timestamps (never client Date.now())
 * - Messages loaded DESC from DB, reversed for display
 * - Temp message timeout: 5s confirmation or show error
 *
 * See docs/MESSAGING_ARCHITECTURE.md for full details
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/features/auth'
import { useSocket } from '@/hooks/useSocket'
import {
  CLIENT_EVENTS,
  SERVER_EVENTS,
  type NewMessagePayload,
  type MessageSentPayload,
  type MessageErrorPayload,
  type ConversationUpdatedPayload,
  type ProductUnlockedPayload,
  type Reaction
} from '@/types/socketEvents'
import {
  MESSAGE_SEND_TIMEOUT_MS,
  TEMP_MESSAGE_PREFIX
} from './constants'

// ============ TYPES ============

interface Message {
  id: string
  /**
   * The optimistic id this message was first rendered under, kept after the
   * server confirms it. Purely a RENDER KEY: `id` is still the real id and is
   * what every other feature keys off.
   *
   * Without this the bubble visibly animates in TWICE on send. The optimistic
   * message mounts under key=tempId and plays animate-slide-in; when
   * MESSAGE_SENT swaps in the real message the key changes to the real id, so
   * React unmounts that bubble and mounts a fresh one, replaying the animation.
   * Between the two the auto-scroll effect fires - which is exactly the
   * "flash, scroll, flash again" that gets reported on mobile.
   */
  clientKey?: string
  conversation_id: string
  sender_id: string
  recipient_id: string
  content: string
  attachments?: any[]
  reactions?: Reaction[]
  reply_to_message_id?: string | null
  reply_to?: {
    id: string
    content: string
    created_at: string
    attachments?: any[]
    sender?: {
      id?: string
      username: string | null
      avatar_url: string | null
    }
  } | null
  // Gallery image reply support
  reply_to_gallery_image_id?: string | null
  reply_to_gallery_image?: {
    id: string
    title: string | null
    thumbnail_url: string
    original_width?: number
    original_height?: number
  } | null
  read_at: string | null
  created_at: string
  sender?: {
    id?: string
    username: string | null
    avatar_url: string | null
  }
}

// Gallery image preview type for reply context
export interface GalleryImagePreview {
  id: string
  title: string | null
  thumbnail_url: string
  original_width: number
  original_height: number
}

interface Conversation {
  id: string
  fan_id: string
  creator_id: string
  status: 'active' | 'archived' | 'flagged'
  unread_count: number
  last_message_at: string
  last_message_content: string | null
  creator?: {
    id: string
    username: string | null
    avatar_url: string | null
    identity_verified?: boolean
  }
}

interface MessagingContextType {
  conversation: Conversation | null
  messages: Message[]
  isLoading: boolean
  error: string | null
  sendMessage: (content: string, attachments?: any[], replyToMessageId?: string, replyToGalleryImageId?: string) => Promise<void>
  toggleReaction: (messageId: string, emoji: string) => void
  loadMoreMessages: () => Promise<void>
  hasMoreMessages: boolean
  isTyping: boolean
  adminIsTyping: boolean
  notifyTyping: () => void
  notifyStoppedTyping: () => void
  replyingTo: Message | null
  startReply: (message: Message) => void
  cancelReply: () => void
  scrollToMessage: (messageId: string) => void
  highlightedMessageId: string | null
  recentlyUnlockedProducts: Set<string>
  // Gallery image reply support
  replyingToGalleryImage: GalleryImagePreview | null
  setReplyingToGalleryImage: (image: GalleryImagePreview | null) => void
}

const MessagingContext = createContext<MessagingContextType | null>(null)

// ============ PROVIDER ============

/**
 * Merge messages fetched from the server into the local list.
 *
 * THE BUG THIS FIXES
 * A sent message is shown optimistically under a temporary id and only removed
 * when the server confirms it - up to 15 seconds later. If the socket
 * reconnects inside that window, the catch-up fetch appends the REAL message,
 * and de-duplication by id alone can never match the temporary one. Both
 * render, and the fan sees their own message twice until the temp times out.
 *
 * So this also retires any pending temp that the incoming batch has clearly
 * confirmed: same sender, same content, within a couple of minutes. The time
 * bound matters - without it, sending the same short reply twice on purpose
 * ("ok", "thanks") would silently swallow the second one.
 */
function mergeServerMessages(prev: Message[], incoming: Message[]): Message[] {
  if (!incoming || incoming.length === 0) return prev

  const existingIds = new Set(prev.map(m => m.id))
  const genuinelyNew = incoming.filter(m => !existingIds.has(m.id))
  if (genuinelyNew.length === 0) return prev

  const CONFIRM_WINDOW_MS = 2 * 60 * 1000
  const confirmedTempIds = new Set<string>()

  for (const temp of prev) {
    if (!temp.id?.startsWith(TEMP_MESSAGE_PREFIX)) continue
    const match = genuinelyNew.find(real =>
      real.sender_id === temp.sender_id &&
      real.content === temp.content &&
      Math.abs(new Date(real.created_at).getTime() - new Date(temp.created_at).getTime()) < CONFIRM_WINDOW_MS &&
      !confirmedTempIds.has(temp.id)
    )
    if (match) confirmedTempIds.add(temp.id)
  }

  const kept = confirmedTempIds.size > 0
    ? prev.filter(m => !confirmedTempIds.has(m.id))
    : prev

  return [...kept, ...genuinelyNew].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  )
}

export function MessagingProvider({ children }: { children: React.ReactNode }) {
  // ---- State ----
  const { user } = useAuth()
  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [isTyping, _setIsTyping] = useState(false)
  // Admin → fan typing indicator. TTL-driven: a fresh typing_start refreshes the
  // 5s timer. If typing_stop arrives OR no refresh, indicator auto-hides.
  const [adminIsTyping, setAdminIsTyping] = useState(false)
  const adminTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Fan → admin typing emit. Debounced: only emit typing_start once per active
  // burst; emit typing_stop on send or after idle.
  const typingEmitStateRef = useRef<{
    active: boolean
    idleTimer: ReturnType<typeof setTimeout> | null
    heartbeat: ReturnType<typeof setInterval> | null
  }>({ active: false, idleTimer: null, heartbeat: null })
  const [replyingTo, setReplyingTo] = useState<Message | null>(null)
  const [replyingToGalleryImage, setReplyingToGalleryImage] = useState<GalleryImagePreview | null>(null)
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null)
  const [recentlyUnlockedProducts, setRecentlyUnlockedProducts] = useState<Set<string>>(new Set())

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

  const getAuthToken = useCallback(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      console.error('[MessagingContext] No auth token found in localStorage')
    }
    return token
  }, [])

  const token = getAuthToken()
  const { socket, isConnected, error: socketError } = useSocket(API_URL, token)

  // Log WebSocket connection status
  useEffect(() => {
    if (socketError) {
      console.error('[MessagingContext] WebSocket error:', socketError)
    }
  }, [isConnected, socketError, socket])

  // ---- Initialize ----
  useEffect(() => {
    if (user && user.role === 'fan') {
      initializeConversation()
    }
  }, [user])

  // ---- Fallback Polling for New Messages (when WebSocket fails) ----
  useEffect(() => {
    // Only poll if we have a conversation but WebSocket is not connected
    if (!conversation?.id || isConnected) return

    const pollInterval = setInterval(async () => {
      try {
        const token = getAuthToken()
        if (!token) return

        // Get latest messages using the correct fan endpoint
        const response = await fetch(
          `${API_URL}/api/messages/${conversation.id}?limit=5`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) return

        const data = await response.json()
        const newMessages = data.data || []

        // Check for new messages not in our current list
        setMessages(prev => mergeServerMessages(prev, newMessages))
      } catch (err) {
        console.error('[MessagingContext] Fallback poll error:', err)
      }
    }, 5000) // Poll every 5 seconds when WebSocket is down

    return () => {
      clearInterval(pollInterval)
    }
  }, [conversation?.id, isConnected, API_URL, getAuthToken])

  // ---- Catch Up on Missed Messages After WebSocket Reconnection ----
  // WHY: Messages sent during a brief disconnect may not be delivered via WebSocket.
  // When connection is restored, fetch latest to fill any gaps.
  const prevConnectedRef = useRef(isConnected)
  useEffect(() => {
    const wasDisconnected = !prevConnectedRef.current
    prevConnectedRef.current = isConnected

    // Only catch up when transitioning from disconnected → connected
    if (!isConnected || !wasDisconnected || !conversation?.id) return

    const catchUp = async () => {
      try {
        const token = getAuthToken()
        if (!token) return

        const response = await fetch(
          `${API_URL}/api/messages/${conversation.id}?limit=20`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (!response.ok) return

        const data = await response.json()
        const newMessages = data.data || []

        setMessages(prev => mergeServerMessages(prev, newMessages))
      } catch (err) {
        console.error('[MessagingContext] Reconnection catch-up error:', err)
      }
    }

    catchUp()
  }, [isConnected, conversation?.id, API_URL, getAuthToken])

  async function initializeConversation() {
    try {
      setIsLoading(true)
      setError(null)

      // Load conversation via backend API (not Supabase!)
      const token = getAuthToken()
      if (!token) {
        console.warn('[MessagingContext] ⚠️ No auth token, cannot initialize conversation')
        setIsLoading(false)
        return
      }

      const response = await fetch(`${API_URL}/api/messages/user/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      const data = await response.json()

      if (response.ok && data.data && data.data.length > 0) {
        // Found existing conversation
        const conv = data.data[0]

        setConversation(conv)
        loadMessages(conv.id, null)

        // Auto-mark on conversation open: fans always mark admin messages as read
        // when they open the chat. Standard WhatsApp/iMessage behavior. Without this,
        // admin never sees "read" on messages they sent before the fan was online.
        // Idempotent endpoint — server filters on read_at IS NULL.
        markConversationReadOnServer(conv.id)
      } else {
        // No conversation yet - show empty state
        setConversation(null)
        setMessages([])
        setIsLoading(false)
      }
    } catch (err) {
      console.error('[MessagingContext] ❌ Failed to initialize conversation:', err)
      // Don't show error - just show empty state
      setConversation(null)
      setMessages([])
      setIsLoading(false)
    }
  }

  // ---- Load Messages (cursor-based pagination) ----
  async function loadMessages(conversationId: string, before: string | null) {
    try {
      // Load messages via backend API (not Supabase!)
      const token = getAuthToken()
      if (!token) {
        console.warn('[MessagingContext] ⚠️ No auth token, cannot load messages')
        return
      }

      let url = `${API_URL}/api/messages/${conversationId}?limit=50`
      if (before) {
        url += `&before=${encodeURIComponent(before)}`
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      const data = await response.json()

      if (response.ok && data.data) {
        setMessages(prev => {
          if (!before) {
            // Initial load - replace all messages (reverse to show oldest first)
            return [...data.data].reverse()
          }
          // Load more - prepend older messages, deduplicate by ID
          const existingIds = new Set(prev.map(m => m.id))
          const newBatch = [...data.data].reverse().filter(m => !existingIds.has(m.id))
          return [...newBatch, ...prev]
        })
        setHasMoreMessages(data.data.length === 50)
      } else {
        console.error('[MessagingContext] ❌ Failed to load messages:', data.error)
        setError('Failed to load messages')
      }
    } catch (err) {
      console.error('[MessagingContext] ❌ Failed to load messages:', err)
      setError('Failed to load messages')
    } finally {
      setIsLoading(false)
    }
  }

  async function loadMoreMessages() {
    if (!conversation || conversation.id === 'temp' || !hasMoreMessages) return
    // Cursor-based: use oldest loaded message's timestamp
    const oldestMessage = messages[0]
    if (!oldestMessage) return
    await loadMessages(conversation.id, oldestMessage.created_at)
  }

  // ---- Send Message ----
  async function sendMessage(content: string, attachments: any[] = [], replyToMessageId?: string, replyToGalleryImageId?: string) {
    if (!user) {
      console.error('[MessagingContext] ❌ No user logged in')
      return
    }

    const tempId = TEMP_MESSAGE_PREFIX + Date.now()

    try {
      setError(null)

      // WHY: Ensure conversation exists BEFORE sending to prevent orphaned messages
      let activeConversation = conversation

      if (!activeConversation || !activeConversation.id || activeConversation.id === 'temp') {
        // Get auth token
        const token = getAuthToken()
        if (!token) {
          setError('Session expired - please logout and login again')
          throw new Error('Not authenticated')
        }

        // Create conversation via backend API
        try {
          const response = await fetch(`${API_URL}/api/messages/user/conversations`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          })

          const data = await response.json()

          if (response.ok && data.data && data.data.length > 0) {
            // Found existing conversation
            activeConversation = data.data[0]
            setConversation(activeConversation)
          } else {
            // No conversation exists - need to create one
            // This happens by sending first message via REST, which creates conversation
            activeConversation = null
          }
        } catch (err) {
          console.error('[MessagingContext] ❌ Failed to check for conversation:', err)
          throw new Error('Failed to initialize conversation')
        }
      }

      // Get reply context for temp message (if replying)
      const replyContext = replyToMessageId
        ? messages.find(m => m.id === replyToMessageId)
        : null

      // Get gallery image context for temp message (if replying to gallery image)
      const galleryImageContext = replyToGalleryImageId && replyingToGalleryImage
        ? replyingToGalleryImage
        : null

      // Optimistic update - show message immediately
      const tempMessage: Message = {
        id: tempId,
        clientKey: tempId,
        conversation_id: activeConversation?.id || 'pending',
        sender_id: user.id,
        recipient_id: 'creator', // Will be set properly by backend
        content,
        attachments,
        reply_to_message_id: replyToMessageId || null,
        reply_to: replyContext
          ? {
              id: replyContext.id,
              content: replyContext.content,
              created_at: replyContext.created_at,
              attachments: replyContext.attachments,
              sender: replyContext.sender
            }
          : null,
        // Gallery image reply context
        reply_to_gallery_image_id: replyToGalleryImageId || null,
        reply_to_gallery_image: galleryImageContext,
        read_at: null,
        created_at: new Date().toISOString(),
        sender: {
          id: user.id,
          username: user.username || null,
          avatar_url: user.avatar_url || null
        }
      }

      setMessages(prev => [...prev, tempMessage])

      // Clear reply state after sending
      if (replyToMessageId) {
        setReplyingTo(null)
      }
      // Clear gallery image reply state after sending
      if (replyToGalleryImageId) {
        setReplyingToGalleryImage(null)
      }

      // Send via WebSocket if connected AND conversation exists, otherwise REST fallback
      if (socket && isConnected && activeConversation?.id) {
        socket.emit(CLIENT_EVENTS.SEND_MESSAGE, {
          conversation_id: activeConversation.id,
          content,
          attachments,
          reply_to_message_id: replyToMessageId || null,
          reply_to_gallery_image_id: replyToGalleryImageId || null,
          tempId
        })

        // WHY: Timeout handles network failures where MESSAGE_SENT never arrives
        const confirmationTimeout = setTimeout(() => {
          console.error('[MessagingContext] ⏱️ WebSocket send timeout - no confirmation received')
          setMessages(prev => prev.filter(m => m.id !== tempId))
          setError('Message send timed out - please try again')
        }, MESSAGE_SEND_TIMEOUT_MS)

        // Store timeout ID so we can clear it when MESSAGE_SENT arrives
        ;(tempMessage as any)._confirmationTimeout = confirmationTimeout

      } else {
        // Fallback to REST
        const token = getAuthToken()
        if (!token) {
          setMessages(prev => prev.filter(m => m.id !== tempId))
          setError('Session expired - please logout and login again')
          throw new Error('Not authenticated')
        }

        const response = await fetch(`${API_URL}/api/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            content,
            attachments,
            reply_to_message_id: replyToMessageId || null,
            reply_to_gallery_image_id: replyToGalleryImageId || null
          })
        })

        const contentType = response.headers.get('content-type')
        if (!contentType || !contentType.includes('application/json')) {
          console.error('[MessagingContext] ❌ Invalid response format from server')
          throw new Error('Server returned invalid response format')
        }

        const data = await response.json()

        if (!response.ok || data.error) {
          console.error('[MessagingContext] ❌ REST send failed:', data.error)
          setMessages(prev => prev.filter(m => m.id !== tempId))
          throw new Error(data.error || `Server error: ${response.status}`)
        }

        // Replace temp message with real one, keeping the original render key
        // so the bubble updates in place instead of remounting. Same reason as
        // the MESSAGE_SENT handler - this is the REST fallback for the identical
        // swap, and it had the identical double-animation.
        setMessages(prev => prev.map(msg =>
          msg.id === tempId ? { ...data.data, clientKey: msg.clientKey || tempId } : msg
        ))

        // If this was the first message, update conversation state
        if (!activeConversation && data.data.conversation_id) {
          initializeConversation()
        }
      }
    } catch (err) {
      console.error('[MessagingContext] ❌ Send message error:', err)

      const errorMessage = err instanceof Error ? err.message : 'Failed to send message'
      setError(errorMessage)

      // Remove temp message on error
      setMessages(prev => prev.filter(msg => msg.id !== tempId))

      throw err
    }
  }

  // ---- Toggle Reaction ----
  function toggleReaction(messageId: string, emoji: string) {
    if (!conversation?.id) {
      console.warn('[MessagingContext] ⚠️ Cannot toggle reaction: no conversation')
      return
    }

    if (!socket || !isConnected) {
      console.warn('[MessagingContext] ⚠️ Cannot toggle reaction: WebSocket not connected')
      setError('Unable to add reaction - connection lost')
      return
    }

    // Emit toggle reaction event to server
    socket.emit(CLIENT_EVENTS.TOGGLE_REACTION, {
      message_id: messageId,
      emoji,
      conversation_id: conversation.id
    })

    // Note: No optimistic update - we wait for server confirmation via REACTION_TOGGLED event
    // This prevents race conditions with single emoji per user constraint
  }

  // ---- Mark Read (REST, idempotent) ----
  // Single write path for fan-side read receipts. Server filter is `read_at IS NULL`
  // so repeat calls are safe no-ops. Failure is silent: client refetch reconciles state.
  const markConversationReadOnServer = useCallback(async (conversationId: string) => {
    try {
      const token = getAuthToken()
      if (!token) return
      await fetch(`${API_URL}/api/messages/conversation/${conversationId}/mark-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
    } catch {
      // Best-effort. Reconciled on next refetch.
    }
  }, [API_URL, getAuthToken])

  // ---- Typing emit (debounced) ----
  const TYPING_IDLE_MS = 4000
  // The receiver hides the indicator 5s after the last event it heard, and
  // typing continuously produces NO further events - so without a repeat the
  // bubble dies mid-sentence and only returns after a pause. The server side
  // already documents the expectation ("debounced, ~1 per 3s"); nothing was
  // sending it. 2500ms leaves margin under the 5s TTL.
  const TYPING_HEARTBEAT_MS = 2500

  const notifyTyping = useCallback(() => {
    if (!socket || !isConnected || !conversation?.id) return

    const state = typingEmitStateRef.current
    if (!state.active) {
      state.active = true
      socket.emit(CLIENT_EVENTS.TYPING_START, { conversation_id: conversation.id })

      // Repeat while they are still typing - see TYPING_HEARTBEAT_MS above.
      if (state.heartbeat) clearInterval(state.heartbeat)
      state.heartbeat = setInterval(() => {
        if (state.active && socket && conversation?.id) {
          socket.emit(CLIENT_EVENTS.TYPING_START, { conversation_id: conversation.id })
        }
      }, TYPING_HEARTBEAT_MS)
    }

    if (state.idleTimer) clearTimeout(state.idleTimer)
    state.idleTimer = setTimeout(() => {
      if (state.heartbeat) { clearInterval(state.heartbeat); state.heartbeat = null }
      if (state.active && socket && conversation?.id) {
        state.active = false
        socket.emit(CLIENT_EVENTS.TYPING_STOP, { conversation_id: conversation.id })
      }
    }, TYPING_IDLE_MS)
  }, [socket, isConnected, conversation?.id])

  const notifyStoppedTyping = useCallback(() => {
    const state = typingEmitStateRef.current
    if (state.idleTimer) {
      clearTimeout(state.idleTimer)
      state.idleTimer = null
    }
    if (state.heartbeat) {
      clearInterval(state.heartbeat)
      state.heartbeat = null
    }
    if (state.active && socket && conversation?.id) {
      state.active = false
      socket.emit(CLIENT_EVENTS.TYPING_STOP, { conversation_id: conversation.id })
    }
  }, [socket, conversation?.id])

  // ---- Reply Functions ----
  function startReply(message: Message) {
    setReplyingTo(message)
  }

  function cancelReply() {
    setReplyingTo(null)
  }

  function scrollToMessage(messageId: string) {
    // Set highlighted message ID
    setHighlightedMessageId(messageId)

    // Trigger scroll via DOM manipulation (component will use this)
    const messageElement = document.getElementById(`message-${messageId}`)
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' })

      // Clear highlight after 2 seconds
      setTimeout(() => {
        setHighlightedMessageId(null)
      }, 2000)
    } else {
      console.warn('[MessagingContext] ⚠️ Message element not found for scrolling')
    }
  }

  // ============ WEBSOCKET EVENTS ============
  useEffect(() => {
    // Set up listeners even if not connected yet - socket.io will queue them
    if (!socket) return

    // Listen for new messages
    const handleNewMessage = (payload: NewMessagePayload) => {
      // Skip NEW_MESSAGE for messages the user sent themselves.
      // Own messages are handled by MESSAGE_SENT (which replaces the temp message).
      // Without this, a race condition between MESSAGE_SENT and NEW_MESSAGE causes duplicates.
      if (payload.message.sender_id === user?.id) {
        return
      }

      if (conversation?.id === payload.conversation_id) {
        setMessages(prev => {
          if (prev.some(m => m.id === payload.message.id)) {
            return prev
          }
          return [...prev, payload.message]
        })

        // Auto-mark as read ONLY if fan is receiving an ADMIN message AND is actively
        // viewing this conversation. Uses idempotent REST endpoint (single write path);
        // the old socket MARK_READ event is deprecated server-side and is a no-op.
        const isAdminMessage = payload.message.sender_id !== user?.id && payload.message.recipient_id === user?.id
        if (isAdminMessage && payload.conversation_id) {
          markConversationReadOnServer(payload.conversation_id)
        }
      }

      // Update conversation metadata
      if (conversation && payload.conversation) {
        setConversation(prev => prev ? {
          ...prev,
          last_message_content: payload.message.content,
          last_message_at: payload.message.created_at
        } : null)
      }
    }

    // Listen for message sent confirmation
    const handleMessageSent = (payload: MessageSentPayload) => {
      if (payload.tempId) {
        setMessages(prev => {
          const tempMsg = prev.find(m => m.id === payload.tempId)
          if (tempMsg && (tempMsg as any)._confirmationTimeout) {
            clearTimeout((tempMsg as any)._confirmationTimeout)
          }
          // Keep the original render key so React updates this bubble in place
          // instead of remounting it and replaying the entry animation.
          return prev.map(msg =>
            msg.id === payload.tempId
              ? { ...payload.message, clientKey: msg.clientKey || payload.tempId }
              : msg
          )
        })
      }
    }

    // Listen for message errors
    const handleMessageError = (payload: MessageErrorPayload & { tempId?: string }) => {
      console.error('[MessagingContext] ❌ MESSAGE_ERROR:', payload.error)

      if (payload.tempId) {
        setMessages(prev => prev.filter(m => m.id !== payload.tempId))
      }

      const errorMessage = payload.details
        ? `${payload.error}: ${payload.details}`
        : payload.error
      setError(errorMessage)
    }

    // Listen for conversation updates
    const handleConversationUpdated = (payload: ConversationUpdatedPayload) => {
      if (conversation?.id === payload.conversation.id) {
        setConversation(prev =>
          prev ? { ...prev, ...payload.conversation } : null
        )
      }
    }

    // Listen for read receipts
    const handleMessageRead = (payload: {
      conversation_id: string
      user_id: string
      read_at?: string
      updated_count?: number
    }) => {
      if (conversation?.id === payload.conversation_id) {
        // WHY: Server timestamp prevents client clock skew causing sync issues
        const serverTimestamp = payload.read_at || new Date().toISOString()

        // Mark messages whose recipient_id matches the user who just read them.
        // - If admin marked fan-sent messages: payload.user_id = creator_id → updates
        //   the fan's SENT messages so the fan sees "read" on what they sent.
        // - If fan marked admin-sent messages: payload.user_id = fan's own id → no-op
        //   on this client (those are the fan's received messages, already shown read).
        setMessages(prev => prev.map(msg => {
          if (!msg.read_at && msg.recipient_id === payload.user_id) {
            return { ...msg, read_at: serverTimestamp }
          }
          return msg
        }))

        setConversation(prev => prev ? { ...prev, unread_count: 0 } : null)
      }
    }

    // Listen for reaction toggles
    const handleReactionToggled = (payload: {
      message_id: string
      conversation_id: string
      action: 'added' | 'removed'
      reaction: Reaction | null
      user_id: string
    }) => {
      if (conversation?.id === payload.conversation_id) {
        setMessages(prev => prev.map(msg => {
          if (msg.id !== payload.message_id) return msg

          const reactions: Reaction[] = msg.reactions || []

          if (payload.action === 'added' && payload.reaction) {
            // Remove any existing reaction from this user (single emoji per user)
            const filtered = reactions.filter((r: Reaction) => r.user_id !== payload.user_id)
            return { ...msg, reactions: [...filtered, payload.reaction] }
          } else if (payload.action === 'removed' && payload.reaction) {
            // Remove the specific reaction
            const reactionId = payload.reaction.id
            return { ...msg, reactions: reactions.filter((r: Reaction) => r.id !== reactionId) }
          }

          return msg
        }))
      }
    }

    // Listen for product unlock (real-time purchase confirmation)
    const handleProductUnlocked = (payload: ProductUnlockedPayload) => {
      // Update messages: mark the product as purchased
      setMessages(prev => prev.map(msg => ({
        ...msg,
        attachments: msg.attachments?.map(att =>
          att.product_id === payload.product_id
            ? { ...att, is_purchased: true }
            : att
        )
      })))

      // Show checkmark briefly (3 seconds)
      setRecentlyUnlockedProducts(prev => new Set(prev).add(payload.product_id))
      setTimeout(() => {
        setRecentlyUnlockedProducts(prev => {
          const next = new Set(prev)
          next.delete(payload.product_id)
          return next
        })
      }, 3000)
    }

    // Admin typing indicator with 5s TTL. Refresh-on-every-event design so a dropped
    // typing_stop just causes the indicator to fade after at most 5s — never sticks.
    const ADMIN_TYPING_TTL_MS = 5000
    const handleAdminTyping = (payload: { conversation_id: string; is_typing: boolean }) => {
      if (conversation?.id !== payload.conversation_id) return

      if (adminTypingTimerRef.current) {
        clearTimeout(adminTypingTimerRef.current)
        adminTypingTimerRef.current = null
      }

      if (payload.is_typing) {
        setAdminIsTyping(true)
        adminTypingTimerRef.current = setTimeout(() => {
          setAdminIsTyping(false)
          adminTypingTimerRef.current = null
        }, ADMIN_TYPING_TTL_MS)
      } else {
        setAdminIsTyping(false)
      }
    }

    // Register listeners
    socket.on(SERVER_EVENTS.NEW_MESSAGE, handleNewMessage)
    socket.on(SERVER_EVENTS.MESSAGE_SENT, handleMessageSent)
    socket.on(SERVER_EVENTS.MESSAGE_ERROR, handleMessageError)
    socket.on(SERVER_EVENTS.CONVERSATION_UPDATED, handleConversationUpdated)
    socket.on(SERVER_EVENTS.MESSAGE_READ, handleMessageRead)
    socket.on(SERVER_EVENTS.REACTION_TOGGLED, handleReactionToggled)
    socket.on(SERVER_EVENTS.PRODUCT_UNLOCKED, handleProductUnlocked)
    socket.on(SERVER_EVENTS.ADMIN_TYPING, handleAdminTyping)

    // Cleanup listeners on unmount
    return () => {
      socket.off(SERVER_EVENTS.NEW_MESSAGE, handleNewMessage)
      socket.off(SERVER_EVENTS.MESSAGE_SENT, handleMessageSent)
      socket.off(SERVER_EVENTS.MESSAGE_ERROR, handleMessageError)
      socket.off(SERVER_EVENTS.CONVERSATION_UPDATED, handleConversationUpdated)
      socket.off(SERVER_EVENTS.MESSAGE_READ, handleMessageRead)
      socket.off(SERVER_EVENTS.REACTION_TOGGLED, handleReactionToggled)
      socket.off(SERVER_EVENTS.PRODUCT_UNLOCKED, handleProductUnlocked)
      socket.off(SERVER_EVENTS.ADMIN_TYPING, handleAdminTyping)
      if (adminTypingTimerRef.current) clearTimeout(adminTypingTimerRef.current)
    }
  }, [socket, isConnected, conversation?.id])

  const value: MessagingContextType = {
    conversation,
    messages,
    isLoading,
    error,
    sendMessage,
    toggleReaction,
    loadMoreMessages,
    hasMoreMessages,
    isTyping,
    adminIsTyping,
    notifyTyping,
    notifyStoppedTyping,
    replyingTo,
    startReply,
    cancelReply,
    scrollToMessage,
    highlightedMessageId,
    recentlyUnlockedProducts,
    // Gallery image reply support
    replyingToGalleryImage,
    setReplyingToGalleryImage
  }

  return (
    <MessagingContext.Provider value={value}>
      <div data-messaging-context="true" style={{ display: 'none' }} />
      {children}
    </MessagingContext.Provider>
  )
}

export function useMessaging() {
  const context = useContext(MessagingContext)
  if (!context) {
    throw new Error('useMessaging must be used within MessagingProvider')
  }
  return context
}
